import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { urlHashDatePicker } from 'constants/url'
import { EmptyObject } from 'lib/object/objectUtils'
import offerPageURL from 'lib/offer/offerPageURL'
import { buildSearchParamsFromFilters } from 'lib/search/searchUtils'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { OfferUnavailableReason } from 'lib/search/constants'

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  unavailableReason: string | undefined
  filters?: App.OfferListFilters
}

function BedbankSearchTileUnavailabilityBanner({
  offer,
  unavailableReason,
  filters = EmptyObject,
}: Props) {
  const intl = useIntl()

  const offerUrl = useMemo(() => {
    const searchParams = buildSearchParamsFromFilters({
      ...filters,
      checkIn: undefined,
      checkOut: undefined,
    })

    if (unavailableReason === OfferUnavailableReason.CAPACITY_EXCEEDED || unavailableReason === OfferUnavailableReason.UNAVAILABLE_FOR_FILTERS_SELECTED) {
      return offerPageURL(offer, searchParams)
    }

    return offerPageURL(offer, searchParams) + urlHashDatePicker
  }, [filters, offer, unavailableReason])

  const capacityExceededLabel = intl.formatMessage({
    id: 'HgNIe2zV4yQDq-3JXe1u',
    defaultMessage: 'Unavailable for the number of guests selected',
    description: 'Bedbank search tile banner when guest count exceeds room capacity',
  })
  const filtersUnavailableLabel = intl.formatMessage({
    id: 'EoYuMVhI1M1zLOWPhJl5',
    defaultMessage: 'Unavailable for your filters',
    description: 'Bedbank search tile banner when offer is unavailable for selected filters',
  })
  const datesUnavailableLabel = intl.formatMessage({
    id: 'lwyeKvlhHKwjImN5550l',
    defaultMessage: 'Unavailable for your dates',
    description: 'Bedbank search tile banner when offer is unavailable for selected dates',
  })
  const viewOfferLabel = intl.formatMessage({
    id: 'dHeVUAeHsSXI-tSum3Yk',
    defaultMessage: 'View offer',
    description: 'CTA button to view bedbank offer details from unavailability banner',
  })
  const viewAvailableDatesLabel = intl.formatMessage({
    id: 'Xv4RTT5q886OKhDLRnRh',
    defaultMessage: 'View available dates',
    description: 'CTA button to view available dates from bedbank unavailability banner',
  })

  const bannerMessage = useMemo(() => {
    if (unavailableReason === OfferUnavailableReason.CAPACITY_EXCEEDED) {
      return capacityExceededLabel
    }
    if (unavailableReason === OfferUnavailableReason.UNAVAILABLE_FOR_FILTERS_SELECTED) {
      return filtersUnavailableLabel
    }
    return datesUnavailableLabel
  }, [unavailableReason, capacityExceededLabel, filtersUnavailableLabel, datesUnavailableLabel])

  const actionButtonText = useMemo(() => {
    if (unavailableReason === OfferUnavailableReason.CAPACITY_EXCEEDED || unavailableReason === OfferUnavailableReason.UNAVAILABLE_FOR_FILTERS_SELECTED) {
      return viewOfferLabel
    }
    return viewAvailableDatesLabel
  }, [unavailableReason, viewOfferLabel, viewAvailableDatesLabel])

  const primaryActionButtonProps = useMemo(() => ({
    children: actionButtonText,
    to: offerUrl,
    target: '_blank' as const,
    rel: 'noopener noreferrer',
  }), [actionButtonText, offerUrl])

  return <MessageBanner
    kind="informational-primary"
    primaryActionButtonProps={primaryActionButtonProps}
    data-testid="bedbank-search-tile-unavailability-banner"
  >
    <b>{bannerMessage}</b>
  </MessageBanner>
}

export default BedbankSearchTileUnavailabilityBanner
